import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type AnalyticsService from 'dashboard/services/analytics';
import type SessionService from 'dashboard/services/-session';
import type CurrentUserService from 'dashboard/services/current-user';
import { queryManager } from 'ember-apollo-client';
import { QueryManager } from '@apollo/client/core/QueryManager';
import type {
  AnalyticsEventName,
  AnalyticsMetadata,
  School,
} from 'dashboard/types';
import sortPurchasers from 'dashboard/utils/sort-purchasers';
import Route from '@ember/routing/route';
import TypeformManagerService from 'dashboard/services/typeform-manager';
import { TypeformModalTabType } from 'dashboard/services/typeform-manager';
import type PawyallService from 'dashboard/services/paywall';

export default class DashboardController extends Controller {
  @queryManager declare apollo: QueryManager<Route>;
  @service declare session: SessionService;
  @service declare currentUser: CurrentUserService;
  @service declare analytics: AnalyticsService;
  @service declare typeformManager: TypeformManagerService;
  @service('paywall') declare paywallService: PawyallService;

  queryParams = ['paywall'];

  @tracked offcanvasOpen = false;
  @tracked userProfileOpen = false;
  @tracked paywall: undefined | boolean = undefined;
  @tracked shouldLockFocus = false;
  @tracked isShowingImportTeachers = false;

  get school(): string {
    return this.currentUser.selectedPurchaserId;
  }

  get isDimmed(): boolean {
    return [this.offcanvasOpen, this.userProfileOpen, this.paywall].some(
      (openState) => openState
    );
  }

  get isDimmedOnFullscreen(): boolean {
    return [this.paywall].some((openState) => openState);
  }

  get schools(): School[] {
    const purchasers = this.currentUser.user?.purchasers;
    return purchasers ? sortPurchasers(purchasers) : purchasers;
  }

  get currentSchool(): School | undefined {
    const currentId = this.school;
    return this.schools?.find(
      (school: School) => currentId && school.id === currentId
    );
  }

  get isCurrentSchoolADistrict() {
    return this.currentSchool?.type === 'District';
  }

  schoolById(schoolId: string | undefined): School | undefined {
    return this.schools?.find(
      (school: School) => this.school && schoolId && school.id === schoolId
    );
  }

  @action openOffcanvas(): void {
    this.offcanvasOpen = true;
    this.userProfileOpen = false;
    this.typeformManager.close();
  }

  @action openUserProfile(): void {
    this.analytics.track('user_profile__opened');
    this.userProfileOpen = true;
    this.offcanvasOpen = false;
    this.typeformManager.close();
  }

  @action closeOffcanvas(): void {
    this.shouldLockFocus = false;
    this.offcanvasOpen = false;
  }

  @action closeUserProfile(): void {
    this.shouldLockFocus = false;
    this.userProfileOpen = false;
  }

  @action async openTypeformModal(
    tab = 'TOOLS' as TypeformModalTabType,
    e: MouseEvent
  ): Promise<void> {
    e.stopPropagation();
    await this.#confirmActiveSubscription();

    this.offcanvasOpen = false;
    this.userProfileOpen = false;

    this.typeformManager.open(tab);
  }

  @action closePaywallModal(e: MouseEvent): void {
    e.stopPropagation();
    this.paywall = undefined;
    this.analytics.track('app__paywall_dismissed');
  }

  @action closeAll(): void {
    this.shouldLockFocus = false;
    this.offcanvasOpen = false;
    this.userProfileOpen = false;
    this.typeformManager.close();
    this.isShowingImportTeachers = false;
  }

  @action didTransitionIn(): void {
    this.shouldLockFocus = true;
  }

  @action async trackEventWithPaywall(
    eventName: AnalyticsEventName,
    options: AnalyticsMetadata,
    e: MouseEvent
  ): Promise<void> {
    if (e) {
      e.preventDefault();
    }
    await this.#confirmActiveSubscription();

    this.analytics.track(eventName, options);
    if (e && e.target) {
      window.location.href = (e.target as HTMLAnchorElement).href;
    }
  }

  @action openFeedbackModal(e: MouseEvent): void {
    e.stopPropagation();
    this.typeformManager.open(TypeformModalTabType.FEEDBACK);
  }

  @action async toggleTeacherImportModal() {
    await this.#confirmActiveSubscription();

    this.isShowingImportTeachers = !this.isShowingImportTeachers;
  }

  /**
   * This request is to confirm the current context has an active subscription.
   * If it fails, the paywall will be shown (via the errorLink in `services/-apollo`).
   */
  async #confirmActiveSubscription() {
    await this.paywallService.ensureActiveSubscription(this.school);
  }
}
